/**
 * History: what is true now, derived from the event log.
 *
 * Simple interface: give it every event you have, in any order, duplicates included.
 * Rich inside: it hides schema upgrades, duplicate syncs, out-of-order arrival from several
 * devices, corrections (voids), and inconsistent data. Inconsistencies are reported as
 * `issues`, never silently resolved and never thrown, so one bad event can't block a sync.
 */
import type { EventEnvelope } from "@hitl/core";
import type { CalendarDate } from "./calendar";
import { readRotationEvent, type RotationEvent, type WriteOffReason } from "./events";

export interface Placement {
  readonly potId: string;
  readonly clientId: string;
  readonly slotId: string;
  readonly placedOn: CalendarDate;
  readonly collectedOn?: CalendarDate;
  /** True when the collection wasn't recorded but a later scan proves the pot was moved. */
  readonly collectionInferred?: boolean;
  readonly override?: { readonly reason: string };
  readonly eventId: string;
}

export interface Rejection {
  readonly potId: string;
  readonly clientId: string;
  readonly slotId: string;
  readonly on: CalendarDate;
  readonly reason?: string;
  readonly eventId: string;
}

export interface WriteOff {
  readonly potId: string;
  readonly on: CalendarDate;
  readonly reason: WriteOffReason;
  readonly note?: string;
  readonly photoId?: string;
  /** Where it stood when written off, if it was placed (e.g. broken on site). */
  readonly at?: { readonly clientId: string; readonly slotId: string };
  readonly eventId: string;
}

export type HistoryIssue =
  | { readonly kind: "unreadable-event"; readonly eventId: string; readonly reason: string }
  | { readonly kind: "void-target-missing"; readonly eventId: string; readonly voidsEventId: string }
  | { readonly kind: "collected-but-not-placed-there"; readonly eventId: string; readonly potId: string; readonly slotId: string }
  | { readonly kind: "pot-moved-without-collection"; readonly eventId: string; readonly potId: string; readonly fromSlotId: string }
  | { readonly kind: "slot-occupant-not-collected"; readonly eventId: string; readonly slotId: string; readonly previousPotId: string }
  | { readonly kind: "already-written-off"; readonly eventId: string; readonly potId: string };

export interface History {
  /** Every placement of a pot, oldest first. Includes placements that are still open. */
  placementsOf(potId: string): readonly Placement[];
  /** Where the pot stands right now, or undefined if it's in stock (or unknown). */
  whereIs(potId: string): Placement | undefined;
  /** The pot standing in a slot right now. */
  occupantOf(slotId: string): Placement | undefined;
  /** The write-off that took this pot out of circulation, if any. */
  writtenOff(potId: string): WriteOff | undefined;
  /** Every placement of every pot, oldest first. */
  readonly placements: readonly Placement[];
  readonly writeOffs: readonly WriteOff[];
  readonly rejections: readonly Rejection[];
  readonly issues: readonly HistoryIssue[];
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** Business day first, then device clock, then a stable per-device order. */
function chronological(a: RotationEvent, b: RotationEvent): number {
  const dayA = "on" in a.payload ? a.payload.on : "";
  const dayB = "on" in b.payload ? b.payload.on : "";
  if (dayA !== dayB) return dayA < dayB ? -1 : 1;
  const clock = Date.parse(a.occurredAt) - Date.parse(b.occurredAt);
  if (clock !== 0 && !Number.isNaN(clock)) return clock;
  if (a.deviceId !== b.deviceId) return a.deviceId < b.deviceId ? -1 : 1;
  return a.deviceSeq - b.deviceSeq;
}

export function buildHistory(rawEvents: readonly EventEnvelope[]): History {
  const issues: HistoryIssue[] = [];

  // 1. Read + dedupe (the same event can sync twice).
  const byId = new Map<string, RotationEvent>();
  for (const raw of rawEvents) {
    if (byId.has(raw.id)) continue;
    const read = readRotationEvent(raw);
    if (read.ok) byId.set(raw.id, read.event);
    else issues.push({ kind: "unreadable-event", eventId: raw.id, reason: read.reason });
  }

  // 2. Apply corrections. Voids target facts; a void can't be voided (record a new fact instead).
  const voided = new Set<string>();
  for (const event of byId.values()) {
    if (event.type !== "event.voided") continue;
    const target = byId.get(event.payload.voidsEventId);
    if (!target || target.type === "event.voided") {
      issues.push({ kind: "void-target-missing", eventId: event.id, voidsEventId: event.payload.voidsEventId });
    } else {
      voided.add(target.id);
    }
  }

  const facts = [...byId.values()]
    .filter((e) => e.type !== "event.voided" && !voided.has(e.id))
    .sort(chronological);

  // 3. Replay facts into placements.
  const placementsByPot = new Map<string, Mutable<Placement>[]>();
  const openByPot = new Map<string, Mutable<Placement>>();
  const openBySlot = new Map<string, Mutable<Placement>>();
  const rejections: Rejection[] = [];
  const writeOffs = new Map<string, WriteOff>();

  const close = (p: Mutable<Placement>, on: CalendarDate, inferred: boolean): void => {
    p.collectedOn = on;
    if (inferred) p.collectionInferred = true;
    openByPot.delete(p.potId);
    openBySlot.delete(p.slotId);
  };

  for (const event of facts) {
    switch (event.type) {
      case "pot.placed": {
        const { potId, clientId, slotId, on, override } = event.payload;
        const elsewhere = openByPot.get(potId);
        if (elsewhere) {
          issues.push({ kind: "pot-moved-without-collection", eventId: event.id, potId, fromSlotId: elsewhere.slotId });
          close(elsewhere, on, true);
        }
        const occupant = openBySlot.get(slotId);
        if (occupant) {
          issues.push({ kind: "slot-occupant-not-collected", eventId: event.id, slotId, previousPotId: occupant.potId });
          close(occupant, on, true);
        }
        const placement: Mutable<Placement> = { potId, clientId, slotId, placedOn: on, eventId: event.id };
        if (override) placement.override = override;
        const list = placementsByPot.get(potId) ?? [];
        list.push(placement);
        placementsByPot.set(potId, list);
        openByPot.set(potId, placement);
        openBySlot.set(slotId, placement);
        break;
      }
      case "pot.collected": {
        const { potId, slotId, on } = event.payload;
        const open = openByPot.get(potId);
        if (open && open.slotId === slotId) close(open, on, false);
        else issues.push({ kind: "collected-but-not-placed-there", eventId: event.id, potId, slotId });
        break;
      }
      case "pot.written-off": {
        const { potId, on, reason, note, photoId } = event.payload;
        if (writeOffs.has(potId)) {
          issues.push({ kind: "already-written-off", eventId: event.id, potId });
          break;
        }
        // Written off where it stood (e.g. broken on site): it is no longer there.
        const standing = openByPot.get(potId);
        if (standing) close(standing, on, true);
        writeOffs.set(potId, {
          potId,
          on,
          reason,
          eventId: event.id,
          ...(note ? { note } : {}),
          ...(photoId ? { photoId } : {}),
          ...(standing ? { at: { clientId: standing.clientId, slotId: standing.slotId } } : {}),
        });
        break;
      }
      case "pot.rejected": {
        const { potId, clientId, slotId, on, reason } = event.payload;
        rejections.push({ potId, clientId, slotId, on, eventId: event.id, ...(reason ? { reason } : {}) });
        break;
      }
    }
  }

  return {
    placementsOf: (potId) => placementsByPot.get(potId) ?? [],
    whereIs: (potId) => openByPot.get(potId),
    occupantOf: (slotId) => openBySlot.get(slotId),
    writtenOff: (potId) => writeOffs.get(potId),
    placements: [...placementsByPot.values()].flat().sort((a, b) => (a.placedOn < b.placedOn ? -1 : a.placedOn > b.placedOn ? 1 : 0)),
    writeOffs: [...writeOffs.values()],
    rejections,
    issues,
  };
}
